import { format, isWithinInterval, parseISO } from 'date-fns';

export const formatWalletAddress = (
  address: string | undefined | null,
  firstBreakpoint = 6,
  secondBreakpoint = 4
): string => {
  if (!address) {
    return '';
  }

  if (address.length > 25) {
    return `${address.slice(0, firstBreakpoint)}...${address.slice(address.length - secondBreakpoint, address.length)}`;
  }

  return address;
};

export const formatCurrency = (amount?: number | string, maximumFractionDigits?: number) => {
  const formattedAmount = Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits });
  const decimals = formattedAmount.split('.')[1];
  return decimals == null && maximumFractionDigits === 2 ? `${formattedAmount}.00` : formattedAmount;
};

export function abbreviateNumber(amount: number | string, maximumFractionDigits = 2) {
  const number = Number(amount || 0);
  if (number === 0) {
    return 0;
  }
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const suffixCount = suffixes.length;
  const tier = Math.floor(Math.log10(Math.abs(number)) / 3);
  const tierIndex = Math.min(tier, suffixCount - 1);
  const scaled = number / Math.pow(10, tierIndex * 3);
  const formatted = parseFloat(scaled.toFixed(maximumFractionDigits)).toString();
  return formatted + suffixes[tierIndex];
}

export const formatNumber = (num: number, decimals = 2): string => {
  const standardFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return standardFormatter.format(num);
};

export const formatDate = (dateStr: string, formatStr: string = 'MM/dd/yyyy'): string => {
  // Parse the date string as UTC to avoid timezone conversion
  const date = new Date(dateStr + 'Z'); // Add 'Z' to treat as UTC
  return format(date, formatStr);
};

export const checkDateInRange = (date: string, startDate: string, endDate: string): boolean => {
  const targetDate = parseISO(date);
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  return isWithinInterval(targetDate, { start, end });
};

export const shortenString = (input: string, maxLength: number = 52): string => {
  if (input.length <= maxLength) {
    return input;
  }
  return input.substring(0, maxLength - 3) + '...';
};

export const scaleNumber = (number: number, decimal: number, fixedDecimals: number = 6): number => {
  if (isNaN(number)) {
    throw new Error('Invalid number string');
  }
  return parseFloat((number / Math.pow(10, decimal)).toFixed(fixedDecimals));
};

export const formatHexStringToScaledNumber = (hex: string, decimal: number): number => {
  const number = parseInt(hex, 16);
  return scaleNumber(number, decimal);
};

export const formatDateToHumanReadable = (date: Date): string => {
  // Ensure the date is treated as UTC to avoid timezone conversion
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return format(utcDate, 'MMMM do yyyy');
};
